import { AssessmentStatus } from "@appally/lib/redux/features/assessmentSlice";
import { AboutYouQuestion } from "./AboutYouQuestion";
import { AreaOfChallenge } from "./AreaOfChallenge";
import { User } from "./User";
import { WorkplaceChallengeQuestion } from "./WorkplaceChallengeQuestion";

type Timestamp = number;

type AssessmentQuestionId = WorkplaceChallengeQuestion["id"];
type AboutYouQuestionId = AboutYouQuestion["id"];
type AreaOfChallengeId = AreaOfChallenge["id"];
type QuestionId = AssessmentQuestionId | AboutYouQuestionId | AreaOfChallengeId;

type UserId = User["id"];

export enum Section {
  about_you = "about_you",
  about_your_job = "about_your_job",
  workplace_challenge = "workplace_challenge",
}

export type ProgressKey =
  | "assessment"
  | "about_you"
  | "about_your_job"
  | "workplace_challenges"
  | "recommendations"
  | "reports"
  | "products"
  | "plan";

export type InvitedAssessmentManager = {
  email: User["email"];
  firstName: User["firstName"];
  lastName: User["lastName"];
  added_at: Timestamp;
  mentalHealthInfo: boolean | null;
  currentLifePressures: boolean | null;
  neuroIdentityInfo: boolean | null;
};

export interface Assessment {
  id: string;
  created_at: Timestamp;
  assessment_user: UserId;
  assigned_helper_user?: UserId;
  under_review?: boolean;
  review_started_at?: Timestamp;
  type?: string; // Don't know what this is
  last_saved?: Timestamp;
  answers?: Answer[];
  progress?: Partial<SectionsProgress>;
  activity?: Activity[];
  regenerate_analysis?: boolean;
  funding_strategy?: string;
  organisation_id?: User["organisation_id"];

  managed_by?: UserId[];
  managers_invited: Record<Timestamp, InvitedAssessmentManager>;
  // managed_by: Record<UserId, AssessmentManager>;
  // 1. Frontend adds a manager's email address to managers_invited
  // 2. Cloud function triggers if field updates:
  // 2.a) Looks to see if manager exists
  // 2.b) If manager exists, adds their user ID to `managed_by` and removes their email from `managers_invited`
  // 2.c) If manager doesn't exist, send an email invite
  // 3. [Cloud function] When a user signs up, check to see if there are any invited manager email address matches and add their user ID to `managed_by` and remove their email from `managers_invited`

  // These fields are generated dynamically in the API
  has_changes?: boolean;
  status?: AssessmentStatus;
}

export type AssessmentWithUser = Assessment & { user: User };

export type SectionsProgress = Record<ProgressKey, SectionProgress>;

export type SectionProgress = {
  started_at: Timestamp;
  completed_at?: Timestamp;
  updated_at?: Timestamp;
  last_updated_by: UserId;
};

export interface Answer {
  question_id: QuestionId;
  section: Section;
  current_value: string | number | (number | string)[];
  new_value: string | number | (number | string)[];
  status: "added" | "changed";
  last_saved: Timestamp;
  is_stress_question?: boolean | null;
}

export interface Activity {
  title: string;
  timestamp: Timestamp;

  // Render on the home page
  self_strategy_id?: string;
  solution_id?: string;
}
